import {
  Cart,
  CartItem,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  ExtraServiceItem,
  GetCartPreviewRequest,
  GetCartPreviewResponse,
} from '@numa-group/checkout-api';
import { UserInfo } from '@numa-group/checkout-api/dist/schemas/user-info.schema';
import axios, { AxiosInstance } from 'axios';

import { IAuthService } from '@src/features/auth/services/i-auth-service';
import { getUserIdAndFingerPrintHeader } from '@src/services/misc/headers';
import { ApiResponse } from '@src/types/api/response/api-response';

const LOCALSTORAGE_ACTIVE_CART_ID = 'TM:active-cartId';
const LOCALSTORAGE_ACTIVE_SESSION_ID = 'TM:active-sessionId';

export class CheckoutService {
  constructor(
    private networkService: AxiosInstance,
    private authService: IAuthService,
  ) {}

  async checkout(prefix: string, createCheckoutSessionRequest: CreateCheckoutSessionRequest) {
    const response = await this.createSession(createCheckoutSessionRequest);
    CheckoutService.storeCartReference(prefix, response.cart.cartId);
    CheckoutService.storeSession(prefix, response.paymentSession);

    return response;
  }

  async checkoutFreeService(cartItem: CartItem, cart: Cart, config?: { user: Partial<UserInfo> }) {
    const response = await this.processFreeServices(cartItem, config);
    return { item: response, cart };
  }

  async previewCart(request: GetCartPreviewRequest) {
    const apiToken = await this.authService.getAuthToken();

    const response = await this.networkService.post<ApiResponse<GetCartPreviewResponse>>('cart-preview', request, {
      headers: { ...(apiToken && { Authorization: `Bearer ${apiToken}` }) },
    });

    return response.data.data;
  }

  async getCart(prefix: string): Promise<Cart | undefined> {
    const cartId = CheckoutService.getActiveCartId(prefix);

    if (!cartId) {
      return undefined;
    }

    try {
      const response = await this.networkService.get<ApiResponse<Cart>>(`cart?id=${cartId}`);
      return response.data.data;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        CheckoutService.removeCartReference(prefix);
      }

      throw e;
    }
  }

  getPaymentSession(prefix: string, paymentSessionId: string) {
    const paymentSessionString = localStorage.getItem(CheckoutService.getSessionKey(prefix));

    if (!paymentSessionString) {
      throw new Error('There is no current session');
    }

    const paymentSession = JSON.parse(paymentSessionString) as CreateCheckoutSessionResponse['paymentSession'];

    if (paymentSession.id !== paymentSessionId) {
      throw new Error('Wrong payment session');
    }

    return paymentSession;
  }

  // returns clonedPaymentMethodId
  async clonePaymentMethod(request: {
    paymentMethodId: string;
    userEmail: string;
    propertyId: string;
  }): Promise<string> {
    const apiToken = await this.authService.getAuthToken();

    const response = await this.networkService.post<ApiResponse<string>>('clone-payment-method', request, {
      headers: {
        ...getUserIdAndFingerPrintHeader('clone-payment-method', { user: { email: request.userEmail } }),
        Authorization: `Bearer ${apiToken}`,
      },
    });
    return response.data.data;
  }

  async deletePaymentMethod(paymentMethodId: string) {
    const apiToken = await this.authService.getAuthToken();
    const response = await this.networkService.delete<ApiResponse<string>>(`payment-method/${paymentMethodId}`, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    });
    return response.data.data;
  }

  async submitArPayment(cartId: string, lang: string, user?: UserInfo) {
    const apiToken = await this.authService.getAuthToken();

    return this.networkService.post(
      `booking/arpayment/${cartId}?preferredLanguage=${lang}`,
      {
        user,
      },
      { headers: { ...(apiToken && { Authorization: `Bearer ${apiToken}` }) } },
    );
  }

  async processPaymentlessAmend(cart: Cart) {
    const apiToken = await this.authService.getAuthToken();

    return this.networkService.post(`/process-paymentless-amend`, cart, {
      headers: { ...(apiToken && { Authorization: `Bearer ${apiToken}` }) },
    });
  }

  private async processFreeServices(cartItem: CartItem, config?: { user: Partial<UserInfo> }) {
    const response = await this.networkService.post<ApiResponse<CartItem<ExtraServiceItem>>>(
      'process-free-services',
      { cartItem },
      {
        headers: getUserIdAndFingerPrintHeader('checkout', config),
      },
    );
    return response.data.data;
  }

  private async createSession(createCheckoutSessionRequest: CreateCheckoutSessionRequest) {
    const apiToken = await this.authService.getAuthToken();

    const response = await this.networkService.post<ApiResponse<CreateCheckoutSessionResponse>>(
      'checkout-session',
      createCheckoutSessionRequest,
      {
        headers: {
          ...getUserIdAndFingerPrintHeader('checkout', createCheckoutSessionRequest),
          Authorization: `Bearer ${apiToken}`,
        },
      },
    );
    return response.data.data;
  }

  private static storeCartReference(prefix: string, cartId?: string) {
    if (!cartId) {
      return;
    }

    localStorage.setItem(CheckoutService.getCartKey(prefix), cartId);
  }

  private static removeCartReference(prefix: string) {
    localStorage.removeItem(CheckoutService.getCartKey(prefix));
  }

  private static getCartKey(prefix: string) {
    return `${LOCALSTORAGE_ACTIVE_CART_ID}:${prefix}`;
  }

  private static getActiveCartId(prefix: string) {
    return localStorage.getItem(CheckoutService.getCartKey(prefix));
  }

  private static storeSession(prefix: string, paymentSession: CreateCheckoutSessionResponse['paymentSession']) {
    localStorage.setItem(CheckoutService.getSessionKey(prefix), JSON.stringify(paymentSession));
  }

  private static getSessionKey(prefix: string) {
    return `${LOCALSTORAGE_ACTIVE_SESSION_ID}:${prefix}`;
  }
}
